/**
 * PurchaseManagerAnalyticsService
 *
 * Central façade for the entire analytics system.
 * Components interact ONLY through this service.
 *
 * Responsibilities:
 *  • Session lifecycle (start / end / timeout)
 *  • Delegates event building to AnalyticsEventBuilder
 *  • Delegates storage to AnalyticsStorageService
 *  • Delegates sync to AnalyticsSyncService
 *  • Delegates funnel tracking to FunnelTrackingService
 *  • Cart-abandon detection via idle timer
 *  • Timing helpers for search-to-add, cart-open-to-proceed, etc.
 *
 * Usage:
 *   constructor(private pmAnalytics: PurchaseManagerAnalyticsService) {}
 *   this.pmAnalytics.trackSearch('hyperpure', { ... });
 */
import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DEFAULT_ANALYTICS_CONFIG } from '../constants/analytics.constants';
import {
    AnalyticsConfig,
    CartCheckoutClickEvent,
    CartRemoveEvent,
    CheckoutMethod,
    FunnelStage,
    GeneratePOClickEvent,
    ProceedToBuyEvent,
    SearchEvent,
    SessionCounters,
    SupplierType
} from '../models/analytics.interfaces';
import { AnalyticsEventBuilder } from './analytics-event-builder.service';
import { AnalyticsStorageService } from './analytics-storage.service';
import { AnalyticsSyncService } from './analytics-sync.service';
import { FunnelTrackingService } from './funnel-tracking.service';

@Injectable()
export class PurchaseManagerAnalyticsService implements OnDestroy {
    private config: AnalyticsConfig = DEFAULT_ANALYTICS_CONFIG;
    private destroy$ = new Subject<void>();
    private abandonTimerSub: Subscription | null = null;
    private isPetpoojaUserFlag = false;
    private petpoojaUserLogged = false;

    // Timing bookmarks (epoch ms)
    private sessionStartTime = 0;
    private lastSearchTime = 0;
    private lastCartActionTime = 0;
    private firstCartAddTime = 0;
    private cartOpenTime = 0;
    private poOpenTime = 0;
    private searchResultTimes = new Map<string, number>(); // keyword → epoch

    // Session counters (in addition to storage persisted ones)
    private searchSequence = 0;
    private poEditSequence = 0;
    private previousKeyword = '';

    constructor(
        private storage: AnalyticsStorageService,
        private builder: AnalyticsEventBuilder,
        private sync: AnalyticsSyncService,
        private funnel: FunnelTrackingService
    ) { }

    ngOnDestroy(): void {
        this.endSession();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ═══════════════════════════════════════════════════════════
    // INITIALISATION
    // ═══════════════════════════════════════════════════════════

    /**
     * Call once when the purchase-manager module loads.
     * Sets user context & kicks off the session + sync loop.
     */
    initialize(
        userContext: {
            userId: string;
            userName: string;
            userEmail: string;
            entityId: string;
            companyId: string;
            companyName: string;
        },
        config?: Partial<AnalyticsConfig>
    ): void {
        // Check if user is from petpooja.com domain
        if (this.isPetpoojaUser(userContext.userEmail)) {
            if (!this.petpoojaUserLogged) {
                console.log('[PM Analytics] User from petpooja.com domain detected. Analytics tracking disabled for this user.');
                this.petpoojaUserLogged = true;
            }
            this.isPetpoojaUserFlag = true;
            return; // Don't initialize analytics
        }

        this.isPetpoojaUserFlag = false;

        if (config) {
            this.config = { ...this.config, ...config };
            this.storage.setConfig(this.config);
            this.sync.configure(this.config);
        }

        this.builder.setUserContext(userContext);
        this.sync.setUserContext(userContext);
        this.startSession();
        this.sync.startSync();
    }

    // ═══════════════════════════════════════════════════════════
    // SESSION
    // ═══════════════════════════════════════════════════════════

    private startSession(): void {
        const sessionId = this.uuid();
        this.sessionStartTime = Date.now();
        this.searchSequence = 0;
        this.poEditSequence = 0;
        this.previousKeyword = '';
        this.firstCartAddTime = 0;
        this.cartOpenTime = 0;
        this.poOpenTime = 0;

        this.builder.setSessionId(sessionId);
        this.storage.setCurrentSessionId(sessionId);
        this.storage.resetSessionCounters();
        this.funnel.setSessionId(sessionId);
        this.funnel.resetForSession();

        const event = this.builder.sessionStart({
            action: 'start',
            userAgent: navigator.userAgent,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            isMobile: window.innerWidth < 768,
        });
        this.storage.addEvent(event);

        this.log('Session started:', sessionId);
    }

    endSession(): void {
        this.stopAbandonTimer();
        this.funnel.markAllDropOff();

        const counters = this.storage.getSessionCounters();
        const event = this.builder.sessionEnd({
            action: 'end',
            durationMs: Date.now() - this.sessionStartTime,
            totalSearches: counters.searchCount,
            totalCartAdds: counters.cartAddCount,
            totalCheckouts: counters.checkoutClickCount,
            totalPOsGenerated: counters.poGeneratedCount,
            pageViewCount: counters.pageViewCount,
            userAgent: navigator.userAgent,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            isMobile: window.innerWidth < 768,
        });
        this.storage.addEvent(event);

        // Force-sync remaining events
        this.sync.triggerSync();
        this.log('Session ended');
    }

    // ═══════════════════════════════════════════════════════════
    // 1. SEARCH ANALYTICS
    // ═══════════════════════════════════════════════════════════

    trackSearch(
        supplier: SupplierType | string,
        params: {
            keyword: string;
            resultCount: number;
            resultCountBySupplier?: Record<string, number>;
            searchSource?: SearchEvent['data']['searchSource'];
            responseTimeMs?: number;
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        this.searchSequence++;
        const now = Date.now();
        const isRepeat = this.previousKeyword === params.keyword;
        const timeSinceLast = this.lastSearchTime
            ? now - this.lastSearchTime
            : undefined;

        const event = this.builder.search(supplier, {
            keyword: params.keyword,
            searchSequenceNumber: this.searchSequence,
            resultCount: params.resultCount,
            resultCountBySupplier: params.resultCountBySupplier || {},
            searchSource: params.searchSource || 'typed',
            responseTimeMs: params.responseTimeMs || 0,
            hasResults: params.resultCount > 0,
            isRepeatSearch: isRepeat,
            previousKeyword: this.previousKeyword || undefined,
            timeSinceLastSearchMs: timeSinceLast,
        });

        this.storage.addEvent(event);
        this.incrementCounter('searchCount');
        this.searchResultTimes.set(params.keyword, now);
        this.previousKeyword = params.keyword;
        this.lastSearchTime = now;

        // Funnel
        this.funnel.reachStage(supplier, FunnelStage.Search);
        if (params.resultCount > 0) {
            this.funnel.reachStage(supplier, FunnelStage.Results);
        }

        this.log('Search tracked:', params.keyword);
    }

    trackSearchItemSelect(
        supplier: SupplierType | string,
        params: {
            keyword: string;
            itemName: string;
            itemId: string;
            itemPrice: number;
            itemUnit: string;
            resultPosition: number;
            isBestPrice: boolean;
            addedToCart: boolean;
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const searchTime = this.searchResultTimes.get(params.keyword) || Date.now();
        const event = this.builder.searchItemSelect(supplier, {
            ...params,
            timeToSelectMs: Date.now() - searchTime,
        });
        this.storage.addEvent(event);
    }

    // ═══════════════════════════════════════════════════════════
    // 2. CART INTERACTION ANALYTICS
    // ═══════════════════════════════════════════════════════════

    trackCartAdd(
        supplier: SupplierType | string,
        params: {
            itemId: string;
            itemName: string;
            quantity: number;
            unitPrice: number;
            itemUnit: string;
            cartValueAfter: number;
            cartItemCountAfter: number;
            fromBestPrice?: boolean;
            hasBulkPricing?: boolean;
            bulkPriceTier?: { qty: number; price: number };
            originKeyword?: string;
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const now = Date.now();
        if (!this.firstCartAddTime) this.firstCartAddTime = now;

        const event = this.builder.cartAdd(supplier, {
            ...params,
            lineTotal: params.quantity * params.unitPrice,
            timeSinceSessionStartMs: now - this.sessionStartTime,
            timeSinceLastCartActionMs: this.lastCartActionTime
                ? now - this.lastCartActionTime
                : undefined,
            fromBestPrice: params.fromBestPrice || false,
            hasBulkPricing: params.hasBulkPricing || false,
        });

        this.storage.addEvent(event);
        this.lastCartActionTime = now;
        this.incrementCounter('cartAddCount');
        this.incrementCounter('cartModificationCount');
        this.resetAbandonTimer();

        // Funnel
        this.funnel.reachStage(supplier, FunnelStage.CartAdd);

        this.log('Cart add:', params.itemName);
    }

    trackCartRemove(
        supplier: SupplierType | string,
        params: {
            itemId: string;
            itemName: string;
            quantityRemoved: number;
            unitPrice: number;
            cartValueAfter: number;
            cartItemCountAfter: number;
            timeInCartMs?: number;
            removalReason?: CartRemoveEvent['data']['removalReason'];
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.cartRemove(supplier, {
            ...params,
            valueLost: params.quantityRemoved * params.unitPrice,
            timeInCartMs: params.timeInCartMs || 0,
            removalReason: params.removalReason || 'user_action',
        });

        this.storage.addEvent(event);
        this.lastCartActionTime = Date.now();
        this.incrementCounter('cartRemoveCount');
        this.incrementCounter('cartModificationCount');
        this.resetAbandonTimer();

        this.log('Cart remove:', params.itemName);
    }

    trackCartQuantityChange(
        supplier: SupplierType | string,
        params: {
            itemId: string;
            itemName: string;
            previousQuantity: number;
            newQuantity: number;
            unitPrice: number;
            cartValueAfter: number;
            crossedBulkThreshold?: boolean;
            effectivePrice?: number;
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const direction =
            params.newQuantity > params.previousQuantity ? 'increase' : 'decrease';
        const priceDelta =
            (params.newQuantity - params.previousQuantity) * params.unitPrice;

        const event = this.builder.cartQuantityChange(supplier, {
            ...params,
            direction,
            priceDelta,
            crossedBulkThreshold: params.crossedBulkThreshold || false,
            timeSinceLastCartActionMs: this.lastCartActionTime
                ? Date.now() - this.lastCartActionTime
                : undefined,
        });

        this.storage.addEvent(event);
        this.lastCartActionTime = Date.now();
        this.incrementCounter('cartModificationCount');
        this.resetAbandonTimer();
    }

    // ═══════════════════════════════════════════════════════════
    // 3. CART CHECKOUT CLICK ANALYTICS
    // ═══════════════════════════════════════════════════════════

    trackCartCheckoutClick(
        supplier: SupplierType | string,
        params: {
            totalCartValue: number;
            totalItems: number;
            uniqueItemCount: number;
            supplierBreakdown: CartCheckoutClickEvent['data']['supplierBreakdown'];
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        this.cartOpenTime = Date.now();
        const counters = this.storage.getSessionCounters();

        const event = this.builder.cartCheckoutClick(supplier, {
            ...params,
            timeFromFirstAddMs: this.firstCartAddTime
                ? Date.now() - this.firstCartAddTime
                : 0,
            sessionDurationMs: Date.now() - this.sessionStartTime,
            cartModificationCount: counters.cartModificationCount,
            searchCount: counters.searchCount,
            averageItemPrice:
                params.totalItems > 0
                    ? params.totalCartValue / params.totalItems
                    : 0,
            maxItemValue: 0, // Caller can override via meta
        });

        this.storage.addEvent(event);
        this.incrementCounter('checkoutClickCount');

        // Funnel
        this.funnel.reachStage(supplier, FunnelStage.CartView);
        this.funnel.reachStage(supplier, FunnelStage.Checkout);

        this.log('Cart checkout click');
    }

    // ═══════════════════════════════════════════════════════════
    // 4. PROCEED TO BUY ANALYTICS
    // ═══════════════════════════════════════════════════════════

    trackProceedToBuy(
        supplier: SupplierType | string,
        params: {
            finalItems: ProceedToBuyEvent['data']['finalItems'];
            finalTotalValue: number;
            finalItemCount: number;
            checkoutMethod: CheckoutMethod;
            targetUrl?: string;
            itemsModifiedAfterCheckout?: number;
            cartReviewed?: boolean;
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;
        const event = this.builder.proceedToBuy(supplier, {
            ...params,
            timeFromCartOpenMs: this.cartOpenTime
                ? Date.now() - this.cartOpenTime
                : 0,
            sessionDurationMs: Date.now() - this.sessionStartTime,
            itemsModifiedAfterCheckout: params.itemsModifiedAfterCheckout || 0,
            cartReviewed: params.cartReviewed ?? true,
        });

        this.storage.addEvent(event);
        this.incrementCounter('proceedToBuyCount');

        // Funnel
        this.funnel.reachStage(supplier, FunnelStage.ProceedToBuy);
        this.funnel.updateFinalValues(
            supplier,
            params.finalTotalValue,
            params.finalItemCount
        );

        this.log('Proceed to buy:', supplier);
    }

    trackProceedToBuyResult(
        supplier: SupplierType | string,
        success: boolean,
        params: {
            checkoutMethod: CheckoutMethod;
            responseTimeMs: number;
            totalItemCount: number;
            totalValue: number;
            errorMessage?: string;
            errorCode?: string;
            failedItemCount?: number;
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.proceedToBuyResult(supplier, success, params);
        this.storage.addEvent(event);

        if (success) {
            this.funnel.reachStage(supplier, FunnelStage.Complete);
            this.incrementCounter('proceedToBuySuccess');
        }

        this.log('Proceed result:', success ? 'SUCCESS' : 'FAILURE');
    }

    // ═══════════════════════════════════════════════════════════
    // 5. LOCAL SUPPLIER PO FLOW ANALYTICS
    // ═══════════════════════════════════════════════════════════

    trackCreatePOOpen(params: {
        localSupplierId: string;
        localSupplierName: string;
        itemCount: number;
        initialPOValue: number;
    }): void {
        if (this.isPetpoojaUserFlag) return;
        this.poOpenTime = Date.now();
        this.poEditSequence = 0;

        const event = this.builder.createPOOpen({
            ...params,
            timeFromCheckoutMs: this.cartOpenTime
                ? Date.now() - this.cartOpenTime
                : 0,
        });
        this.storage.addEvent(event);

        this.funnel.reachStage(SupplierType.Local, FunnelStage.CreatePO);
        this.log('Create PO opened');
    }

    trackPOFieldInteraction(params: {
        fieldName: string;
        interactionType: 'focus' | 'change' | 'blur';
        hasValidationError: boolean;
        validationMessage?: string;
        fieldDurationMs?: number;
    }): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.poFieldInteraction(params);
        this.storage.addEvent(event);
    }

    trackPOQuantityEdit(params: {
        itemId: string;
        itemName: string;
        previousQuantity: number;
        newQuantity: number;
        unitPrice: number;
        revisedPOTotal: number;
    }): void {
        if (this.isPetpoojaUserFlag) return;

        this.poEditSequence++;
        const event = this.builder.poQuantityEdit({
            ...params,
            direction:
                params.newQuantity > params.previousQuantity ? 'increase' : 'decrease',
            editSequenceNumber: this.poEditSequence,
        });
        this.storage.addEvent(event);
    }

    trackPOItemRemove(params: {
        itemId: string;
        itemName: string;
        quantity: number;
        unitPrice: number;
        revisedPOTotal: number;
        remainingItemCount: number;
    }): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.poItemRemove({
            ...params,
            lineTotal: params.quantity * params.unitPrice,
        });
        this.storage.addEvent(event);
    }

    trackGeneratePOClick(params: {
        localSupplierId: string;
        localSupplierName: string;
        finalItems: GeneratePOClickEvent['data']['finalItems'];
        finalPOValue: number;
        finalItemCount: number;
        totalModifications: number;
    }): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.generatePOClick({
            ...params,
            timeInPOFormMs: this.poOpenTime ? Date.now() - this.poOpenTime : 0,
            fullFunnelTimeMs: this.sessionStartTime
                ? Date.now() - this.sessionStartTime
                : 0,
        });
        this.storage.addEvent(event);

        this.funnel.reachStage(SupplierType.Local, FunnelStage.GeneratePO);
        this.funnel.updateFinalValues(
            SupplierType.Local,
            params.finalPOValue,
            params.finalItemCount
        );
        this.incrementCounter('poGeneratedCount');

        this.log('Generate PO clicked');
    }

    trackPOResult(
        success: boolean,
        params: {
            localSupplierId: string;
            localSupplierName: string;
            poValue: number;
            itemCount: number;
            responseTimeMs: number;
            errorMessage?: string;
            poNumber?: string;
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.poResult(success, {
            ...params,
            totalFunnelTimeMs: this.sessionStartTime
                ? Date.now() - this.sessionStartTime
                : 0,
        });
        this.storage.addEvent(event);

        if (success) {
            this.funnel.reachStage(SupplierType.Local, FunnelStage.Complete);
        }

        this.log('PO result:', success ? 'SUCCESS' : 'FAILURE');
    }

    // ═══════════════════════════════════════════════════════════
    // PAGE VIEW
    // ═══════════════════════════════════════════════════════════

    trackPageView(
        pageRoute: string,
        pageName: string,
        previousPageDurationMs?: number
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.pageView({
            pageRoute,
            pageName,
            previousPageDurationMs,
        });
        this.storage.addEvent(event);
        this.incrementCounter('pageViewCount');

        // Landing funnel for all suppliers
        if (pageRoute.includes('purchase-manager') && !pageRoute.includes('price-discovery')) {
            // Landing page
            for (const s of Object.values(SupplierType)) {
                this.funnel.reachStage(s, FunnelStage.Landing);
            }
        }
    }

    // ═══════════════════════════════════════════════════════════
    // QUICK-ADD & COMPARISON
    // ═══════════════════════════════════════════════════════════

    trackQuickAddCategoryView(
        category: 'most_purchased' | 'recent' | 'last_searched' | 'most_searched' | 'top_ordered',
        itemCount: number
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.quickAddCategoryView({ category, itemCount });
        this.storage.addEvent(event);
    }

    trackQuickAddItemSelect(params: {
        category: string;
        itemName: string;
        itemId?: string;
        position: number;
    }): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.quickAddItemSelect(params);
        this.storage.addEvent(event);
    }

    trackPriceComparisonView(
        supplier: SupplierType | string,
        params: {
            keyword: string;
            suppliersCompared: string[];
            bestPriceSupplier: string;
            bestPrice: number;
            priceRange: { min: number; max: number };
            maxSavings: number;
            maxSavingsPercent: number;
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.priceComparisonView(supplier, params);
        this.storage.addEvent(event);
    }

    // ═══════════════════════════════════════════════════════════
    // CART ABANDON DETECTION
    // ═══════════════════════════════════════════════════════════

    private resetAbandonTimer(): void {
        this.stopAbandonTimer();

        this.abandonTimerSub = timer(this.config.cartAbandonTimeoutMs)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.detectCartAbandon();
            });
    }

    private stopAbandonTimer(): void {
        this.abandonTimerSub?.unsubscribe();
        this.abandonTimerSub = null;
    }

    private detectCartAbandon(): void {
        // This is called when the idle timer fires.
        // Actual abandoned items should be provided by the component.
        // We emit a generic abandon event; components can call with real data.
        this.log('Cart abandon timeout fired');
    }

    /**
     * Call from component when navigating away with items still in cart.
     */
    trackCartAbandon(
        supplier: SupplierType | string,
        params: {
            abandonedItems: Array<{
                itemId: string;
                itemName: string;
                quantity: number;
                unitPrice: number;
                lineTotal: number;
            }>;
            totalAbandonedValue: number;
            abandonReason: 'navigation' | 'timeout' | 'browser_close' | 'unknown';
        }
    ): void {
        if (this.isPetpoojaUserFlag) return;

        const event = this.builder.cartAbandon(supplier, {
            ...params,
            totalAbandonedItems: params.abandonedItems.length,
            idleDurationMs: this.lastCartActionTime
                ? Date.now() - this.lastCartActionTime
                : 0,
            sessionDurationMs: Date.now() - this.sessionStartTime,
        });
        this.storage.addEvent(event);

        this.funnel.markDropOff(supplier);
        this.log('Cart abandon tracked');
    }

    // ═══════════════════════════════════════════════════════════
    // FUNNEL QUERIES (Exposed for components / dashboards)
    // ═══════════════════════════════════════════════════════════

    getFunnelSnapshot(supplier: SupplierType | string) {
        return this.funnel.getFunnel(supplier);
    }

    getConversionRate(from: FunnelStage, to: FunnelStage) {
        return this.funnel.getConversionRate(from, to);
    }

    getDropOffDistribution() {
        return this.funnel.getDropOffDistribution();
    }

    // ═══════════════════════════════════════════════════════════
    // MANUAL SYNC CONTROL
    // ═══════════════════════════════════════════════════════════

    forceSync(): void {
        this.sync.triggerSync();
    }

    async flushAndSync(): Promise<void> {
        await this.sync.flushSync();
    }

    // ═══════════════════════════════════════════════════════════
    // HELPERS
    // ═══════════════════════════════════════════════════════════

    private incrementCounter(key: keyof SessionCounters): void {
        this.storage.updateSessionCounters((c) => {
            const value = c[key];
            if (typeof value === 'number') {
                (c as any)[key] = value + 1;
            }
            return c;
        });
    }

    private log(...args: any[]): void {
        if (this.config.debugMode) {
            console.log('[PM Analytics]', ...args);
        }
    }

    private uuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    /**
     * Check if user email belongs to petpooja.com domain
     */
    private isPetpoojaUser(email: string): boolean {
        if (!email) return false;
        return email.toLowerCase().endsWith('@petpooja.com');
    }
}
